import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (txt) => (txt || "").split("\n").map((s) => s.trim()).filter(Boolean);

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  const Icon = Icons[icon] || Icons.FileText;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`pp-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center"><Icon className="h-4 w-4 text-blue-600" /></span>
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function PracticeProfile() {
  const { user } = useAuth();
  const role = user?.role;
  const canPublish = ["administrator", "partner"].includes(role);

  const [loading, setLoading] = useState(true);
  const [published, setPublished] = useState(null);
  const [draft, setDraft] = useState(null);
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [sections, setSections] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/practice-profile");
      setPublished(data.published || null);
      setDraft(data.draft || null);
      setSections(JSON.parse(JSON.stringify((data.draft || data.published || {}).sections || {})));
      const [v, a] = await Promise.all([api.get("/practice-profile/versions"), api.get("/practice-profile/audit")]);
      setVersions(v.data.versions || []);
      setAudit(a.data.audit || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load Practice Profile");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upd = (path, value) => {
    setSections((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let o = next;
      for (let i = 0; i < path.length - 1; i++) { o[path[i]] = o[path[i]] || {}; o = o[path[i]]; }
      o[path[path.length - 1]] = value;
      return next;
    });
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put("/practice-profile/draft", { sections, notes });
      toast.success("Draft saved (does not affect Allison or Penny until published)");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api.post("/practice-profile/draft/approve", { notes }); toast.success("Draft approved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); } finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true);
    try { await api.post("/practice-profile/draft/publish", { notes }); toast.success("Published — now live for Allison & Penny"); setNotes(""); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); } finally { setBusy(false); }
  };

  const fi = sections?.firm_identity || {};
  const pa = sections?.practice_areas || {};
  const pb = sections?.practice_boundaries || {};
  const cb = sections?.cross_border_policy || {};
  const ep = sections?.engagement_philosophy || {};
  const draftApproved = !!draft?.approved_by;

  if (loading || !sections) return <div className="p-8 text-slate-500" data-testid="pp-loading">Loading Practice Profile…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="practice-profile-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Icons.Building2 className="h-6 w-6 text-blue-600" />
            <h1 className="font-heading text-2xl font-semibold text-slate-900">Practice Profile</h1>
            {published && <Badge variant="secondary" data-testid="pp-published-badge">Published v{published.version}</Badge>}
          </div>
          <p className="text-slate-500 mt-1 text-sm">The firm's authoritative institutional knowledge — how we operate, what we do, our boundaries. Allison and Penny reason from the <b>published</b> version. Management-only.</p>
        </div>
      </div>

      {draft && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2" data-testid="pp-draft-banner">
          <Icons.PencilRuler className="h-4 w-4" />
          You are editing a draft {draftApproved ? "(approved — ready to publish)" : "(not yet approved)"}. Draft changes do not affect Allison or Penny until published.
        </div>
      )}

      <Tabs defaultValue="profile">
        <TabsList data-testid="pp-tabs">
          <TabsTrigger value="profile" data-testid="pp-tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="history" data-testid="pp-tab-history">Version History</TabsTrigger>
          <TabsTrigger value="audit" data-testid="pp-tab-audit">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4 mt-4">
          <SectionCard title="Firm Identity" icon="Landmark">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Legal Entity"><Input value={fi.legal_entity || ""} onChange={(e) => upd(["firm_identity", "legal_entity"], e.target.value)} data-testid="pp-legal-entity" /></Field>
              <Field label="Public Brand"><Input value={fi.public_brand || ""} onChange={(e) => upd(["firm_identity", "public_brand"], e.target.value)} /></Field>
              <Field label="AI Platform"><Input value={fi.ai_platform || ""} onChange={(e) => upd(["firm_identity", "ai_platform"], e.target.value)} /></Field>
              <Field label="Operating Brands (one per line)"><Textarea rows={3} value={toLines(fi.operating_brands)} onChange={(e) => upd(["firm_identity", "operating_brands"], fromLines(e.target.value))} /></Field>
            </div>
          </SectionCard>

          <SectionCard title="Core Practice Areas" icon="Layers">
            {["united_states", "thailand"].map((k) => (
              <div key={k} className="grid sm:grid-cols-3 gap-4 border-t border-slate-100 pt-3 first:border-0 first:pt-0">
                <Field label="Jurisdiction"><Input value={pa[k]?.label || ""} onChange={(e) => upd(["practice_areas", k, "label"], e.target.value)} /></Field>
                <Field label="Status"><Input value={pa[k]?.status || ""} onChange={(e) => upd(["practice_areas", k, "status"], e.target.value)} /></Field>
                <div className="sm:col-span-3"><Field label="Areas (one per line)"><Textarea rows={5} value={toLines(pa[k]?.areas)} onChange={(e) => upd(["practice_areas", k, "areas"], fromLines(e.target.value))} data-testid={`pp-areas-${k}`} /></Field></div>
              </div>
            ))}
          </SectionCard>

          <SectionCard title="Practice Boundaries" icon="ShieldAlert">
            {["united_states_irs", "thailand_revenue"].map((k) => (
              <div key={k} className="grid sm:grid-cols-2 gap-4 border-t border-slate-100 pt-3 first:border-0 first:pt-0">
                <div className="sm:col-span-2"><Field label="Label"><Input value={pb[k]?.label || ""} onChange={(e) => upd(["practice_boundaries", k, "label"], e.target.value)} /></Field></div>
                <Field label="Performs (one per line)"><Textarea rows={4} value={toLines(pb[k]?.performs)} onChange={(e) => upd(["practice_boundaries", k, "performs"], fromLines(e.target.value))} /></Field>
                <Field label="Does NOT perform (one per line)"><Textarea rows={4} value={toLines(pb[k]?.does_not_perform)} onChange={(e) => upd(["practice_boundaries", k, "does_not_perform"], fromLines(e.target.value))} /></Field>
              </div>
            ))}
          </SectionCard>

          <SectionCard title="Cross-Border Policy" icon="Globe2">
            <Field label="Governing Rule"><Textarea rows={2} value={cb.rule || ""} onChange={(e) => upd(["cross_border_policy", "rule"], e.target.value)} data-testid="pp-cb-rule" /></Field>
            <Field label="Does NOT do"><Textarea rows={2} value={cb.does_not || ""} onChange={(e) => upd(["cross_border_policy", "does_not"], e.target.value)} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Supported examples (one per line)"><Textarea rows={4} value={toLines(cb.supported_examples)} onChange={(e) => upd(["cross_border_policy", "supported_examples"], fromLines(e.target.value))} /></Field>
              <Field label="Not supported examples (one per line)"><Textarea rows={4} value={toLines(cb.not_supported_examples)} onChange={(e) => upd(["cross_border_policy", "not_supported_examples"], fromLines(e.target.value))} /></Field>
            </div>
          </SectionCard>

          <SectionCard title="Engagement Philosophy" icon="Compass">
            <Field label="Principles (one per line)"><Textarea rows={6} value={toLines(ep.principles)} onChange={(e) => upd(["engagement_philosophy", "principles"], fromLines(e.target.value))} data-testid="pp-principles" /></Field>
          </SectionCard>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-3" data-testid="pp-governance">
            <Field label="Change notes (optional)"><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What changed and why" data-testid="pp-notes" /></Field>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={saveDraft} disabled={busy} data-testid="pp-save-draft"><Icons.Save className="h-4 w-4 mr-1.5" />Save Draft</Button>
              {canPublish && <Button variant="outline" onClick={approve} disabled={busy || !draft || draftApproved} data-testid="pp-approve"><Icons.CheckCheck className="h-4 w-4 mr-1.5" />{draftApproved ? "Approved" : "Approve Draft"}</Button>}
              {canPublish && <Button onClick={publish} disabled={busy || !draft || !draftApproved} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pp-publish"><Icons.UploadCloud className="h-4 w-4 mr-1.5" />Publish</Button>}
              {!canPublish && <span className="text-xs text-slate-500">Managers can draft; an administrator or partner approves and publishes.</span>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="pp-versions">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <Badge variant={v.status === "published" ? "default" : "secondary"}>{v.status === "draft" ? "Draft" : `v${v.version}`}</Badge>
                  <span className="text-slate-600">{v.status}</span>
                  {v.notes && <span className="text-slate-400 truncate max-w-md">— {v.notes}</span>}
                </div>
                <span className="text-xs text-slate-400">{v.published_at ? new Date(v.published_at).toLocaleString() : (v.updated_at ? new Date(v.updated_at).toLocaleString() : "")}</span>
              </div>
            ))}
            {versions.length === 0 && <div className="p-4 text-sm text-slate-400">No versions yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="pp-audit">
            {audit.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  <Icons.Dot className="h-4 w-4 text-slate-300" />
                  <span className="font-medium text-slate-700">{a.event}</span>
                  <span className="text-slate-400">by {a.actor}{a.version ? ` · ${a.version === "draft" ? "draft" : "v" + a.version}` : ""}</span>
                </div>
                <span className="text-xs text-slate-400">{a.at ? new Date(a.at).toLocaleString() : ""}</span>
              </div>
            ))}
            {audit.length === 0 && <div className="p-4 text-sm text-slate-400">No audit events yet.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
